import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import {
  MagnifyingGlass, Funnel, Globe, ShieldCheck, Heartbeat,
  Lightning, CircleNotch, Key, Plugs, ArrowRight, Plus,
} from '@phosphor-icons/react';

const STATUS_STYLES = {
  active: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Active' },
  approved: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', label: 'Approved' },
  suspended: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'Suspended' },
};

const TRUST_STYLES = {
  verified_partner: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Verified Partner' },
  external_registered: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Registered' },
  unverified_remote: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'Unverified' },
  internal_trusted: { bg: 'bg-purple-500/15', text: 'text-purple-400', label: 'Internal' },
};

const HEALTH_STYLES = {
  healthy: { color: 'text-emerald-400', label: 'Healthy' },
  degraded: { color: 'text-yellow-400', label: 'Degraded' },
  unreachable: { color: 'text-red-400', label: 'Unreachable' },
  unknown: { color: 'text-[#718096]', label: 'Unknown' },
};

const AUTH_LABELS = {
  api_key: 'API Key',
  oauth: 'OAuth',
  none: 'None',
  bearer: 'Bearer',
  hmac: 'HMAC',
};

function ExternalAgentLibrary() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [trustFilter, setTrustFilter] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [stats, setStats] = useState({});
  const [showRegister, setShowRegister] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (search) params.set('search', search);
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/list?${params}`);
      if (res.ok) {
        const data = await res.json();
        const items = data.items || data.federations || data;
        const list = Array.isArray(items) ? items : [];
        // Only show active/approved agents in the library
        const filtered = list.filter((a) =>
          ['active', 'approved'].includes(a.status)
        );
        setAgents(filtered);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/admin/queue/stats`);
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchAgents(); fetchStats(); }, [fetchAgents, fetchStats]);

  const filtered = agents.filter((a) => {
    if (trustFilter && a.trust_class !== trustFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (a.name || '').toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q) ||
        (a.canonical_capabilities || []).some((c) => c.toLowerCase().includes(q))
      );
    }
    return true;
  });

  return (
    <div data-testid="external-agent-library" className="min-h-screen bg-[var(--surface-0)] px-6 py-8 lg:px-10">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              External Agent Fabric
            </h1>
            <p className="text-sm text-[#718096] mt-1">
              Browse and discover external agents connected to the platform
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowRegister(true)}
              data-testid="add-external-agent-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-sm font-medium transition-colors"
            >
              <Plus size={16} weight="bold" /> Add External Agent
            </button>
            <StatChip label="Active" value={stats.active || 0} color="emerald" />
            <StatChip label="Approved" value={stats.approved || 0} color="blue" />
            <StatChip label="Total" value={stats.total || 0} color="gray" />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
            <input
              data-testid="agent-library-search"
              type="text"
              placeholder="Search agents or capabilities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-sm text-white placeholder-[#718096] focus:outline-none focus:border-[#718096]"
            />
          </div>
          <div className="relative">
            <Funnel size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
            <select
              data-testid="agent-library-trust-filter"
              value={trustFilter}
              onChange={(e) => setTrustFilter(e.target.value)}
              className="pl-8 pr-8 py-2 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-sm text-white appearance-none focus:outline-none cursor-pointer"
            >
              <option value="">All Trust</option>
              <option value="verified_partner">Verified Partner</option>
              <option value="external_registered">Registered</option>
              <option value="internal_trusted">Internal</option>
              <option value="unverified_remote">Unverified</option>
            </select>
          </div>
        </div>

        {/* Agent Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={24} className="animate-spin text-[#718096]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Globe size={40} className="mx-auto text-[#5C6778] mb-3" />
            <p className="text-[#718096] text-sm">No external agents found</p>
            <p className="text-[#5C6778] text-xs mt-1">Agents will appear here once registered and approved</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((agent) => (
              <AgentCard
                key={agent.federation_id}
                agent={agent}
                onClick={() => setSelectedAgent(agent)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selectedAgent && (
        <AgentDetailPanel
          agent={selectedAgent}
          onClose={() => setSelectedAgent(null)}
        />
      )}

      {/* Register External Agent Modal */}
      {showRegister && (
        <RegisterAgentModal
          onClose={() => setShowRegister(false)}
          onSuccess={() => { setShowRegister(false); fetchAgents(); fetchStats(); }}
        />
      )}
    </div>
  );
}

function StatChip({ label, value, color }) {
  const colors = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    gray: 'bg-[var(--surface-2)] text-[#A0AEC0] border-[var(--border-subtle)]',
  };
  return (
    <div className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${colors[color] || colors.gray}`}>
      {value} {label}
    </div>
  );
}

function AgentCard({ agent, onClick }) {
  const status = STATUS_STYLES[agent.status] || STATUS_STYLES.active;
  const trust = TRUST_STYLES[agent.trust_class] || TRUST_STYLES.unverified_remote;
  const health = HEALTH_STYLES[agent.health_status] || HEALTH_STYLES.unknown;
  const auth = AUTH_LABELS[agent.auth_method] || agent.auth_method || 'Unknown';
  const caps = agent.canonical_capabilities || [];

  return (
    <button
      data-testid={`agent-card-${agent.federation_id}`}
      onClick={onClick}
      className="w-full text-left p-4 rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)] hover:border-[rgba(255,255,255,0.15)] transition-all duration-200 group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white truncate group-hover:text-blue-300 transition-colors">
            {agent.name}
          </h3>
          {agent.developer_name && (
            <p className="text-xs text-[#718096] mt-0.5">{agent.developer_name}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 ml-2">
          <Heartbeat size={12} className={health.color} />
          <span className={`text-[10px] font-medium ${status.text} px-1.5 py-0.5 rounded ${status.bg}`}>
            {status.label}
          </span>
        </div>
      </div>

      {agent.description && (
        <p className="text-xs text-[#A0AEC0] line-clamp-2 mb-3">{agent.description}</p>
      )}

      {/* Capabilities */}
      {caps.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {caps.slice(0, 4).map((c) => (
            <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--surface-2)] border border-[var(--border-subtle)] text-[#A0AEC0]">
              {c}
            </span>
          ))}
          {caps.length > 4 && (
            <span className="text-[10px] px-1.5 py-0.5 text-[#718096]">+{caps.length - 4}</span>
          )}
        </div>
      )}

      {/* Footer: Trust + Auth */}
      <div className="flex items-center justify-between pt-2 border-t border-[var(--border-subtle)]">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${trust.bg} ${trust.text}`}>
            <ShieldCheck size={10} className="inline mr-0.5" weight="fill" />
            {trust.label}
          </span>
          <span className="text-[10px] text-[#718096]">
            <Key size={10} className="inline mr-0.5" />
            {auth}
          </span>
        </div>
        <ArrowRight size={14} className="text-[#5C6778] group-hover:text-[#718096] transition-colors" />
      </div>
    </button>
  );
}

function AgentDetailPanel({ agent, onClose }) {
  const status = STATUS_STYLES[agent.status] || STATUS_STYLES.active;
  const trust = TRUST_STYLES[agent.trust_class] || TRUST_STYLES.unverified_remote;
  const health = HEALTH_STYLES[agent.health_status] || HEALTH_STYLES.unknown;
  const caps = agent.canonical_capabilities || [];
  const contract = agent.contract || {};
  const invoke = contract.invoke || {};
  const inputSchema = invoke.input_schema || agent.input_schema || {};
  const outputSchema = invoke.output_schema || agent.output_schema || {};

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="agent-detail-panel" className="relative w-full max-w-lg bg-[var(--surface-1)] border-l border-[var(--border-subtle)] overflow-y-auto">
        <div className="sticky top-0 bg-[var(--surface-1)] border-b border-[var(--border-subtle)] px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">{agent.name}</h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors">
              <span className="text-[#718096] text-lg">&times;</span>
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-xs font-medium px-2 py-0.5 rounded border ${status.bg} ${status.text} ${status.border}`}>
              {status.label}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${trust.bg} ${trust.text}`}>
              <ShieldCheck size={12} className="inline mr-0.5" weight="fill" />
              {trust.label}
            </span>
            <span className={`text-xs ${health.color}`}>
              <Heartbeat size={12} className="inline mr-0.5" />
              {health.label}
            </span>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Description */}
          {agent.description && (
            <Section title="Description">
              <p className="text-sm text-[#A0AEC0]">{agent.description}</p>
            </Section>
          )}

          {/* Developer */}
          {agent.developer_name && (
            <Section title="Developer">
              <p className="text-sm text-white">{agent.developer_name}</p>
            </Section>
          )}

          {/* Endpoint */}
          <Section title="Endpoint">
            <div className="text-xs font-mono bg-[var(--surface-2)] p-2 rounded border border-[var(--border-subtle)] text-[#A0AEC0] break-all">
              {invoke.method || 'POST'} {invoke.endpoint_url || agent.endpoint_url || '—'}
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-[#718096]">
              <span>Timeout: {invoke.timeout_ms || '30000'}ms</span>
              <span>Auth: {AUTH_LABELS[agent.auth_method] || agent.auth_method}</span>
            </div>
          </Section>

          {/* Capabilities */}
          <Section title="Capabilities">
            <div className="flex flex-wrap gap-1.5">
              {caps.map((c) => (
                <span key={c} className="text-xs px-2 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300">
                  <Lightning size={10} className="inline mr-1" />
                  {c}
                </span>
              ))}
              {caps.length === 0 && <p className="text-xs text-[#718096]">No capabilities declared</p>}
            </div>
          </Section>

          {/* Input Schema */}
          {Object.keys(inputSchema).length > 0 && (
            <Section title="Input Schema">
              <pre className="text-xs font-mono bg-[var(--surface-2)] p-3 rounded border border-[var(--border-subtle)] text-[#A0AEC0] overflow-x-auto max-h-40">
                {JSON.stringify(inputSchema, null, 2)}
              </pre>
            </Section>
          )}

          {/* Output Schema */}
          {Object.keys(outputSchema).length > 0 && (
            <Section title="Output Schema">
              <pre className="text-xs font-mono bg-[var(--surface-2)] p-3 rounded border border-[var(--border-subtle)] text-[#A0AEC0] overflow-x-auto max-h-40">
                {JSON.stringify(outputSchema, null, 2)}
              </pre>
            </Section>
          )}

          {/* Tags */}
          {agent.tags?.length > 0 && (
            <Section title="Tags">
              <div className="flex flex-wrap gap-1.5">
                {agent.tags.map((t) => (
                  <span key={t} className="text-xs px-2 py-0.5 rounded bg-[var(--surface-2)] text-[#718096]">
                    {t}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Audit Log */}
          {agent.audit_log?.length > 0 && (
            <Section title="Activity Log">
              <div className="space-y-1.5">
                {agent.audit_log.slice(-5).reverse().map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-[#718096]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5C6778]" />
                    <span className="text-[#A0AEC0] font-medium">{entry.action}</span>
                    <span>{new Date(entry.timestamp).toLocaleDateString()}</span>
                    {entry.notes && <span className="text-[#5C6778]">— {entry.notes}</span>}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Register External Agent Modal ── */
function RegisterAgentModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    name: '', endpoint_url: '', description: '', auth_method: 'api_key',
    capabilities: '', trust_class: 'external_registered',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.endpoint_url.trim()) {
      setError('Name and Endpoint URL are required.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        ...form,
        capabilities: form.capabilities ? form.capabilities.split(',').map(c => c.trim()).filter(Boolean) : [],
      };
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        onSuccess();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Registration failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)] text-sm text-white placeholder-[#718096] focus:outline-none focus:border-[#00E5C0]';
  const labelCls = 'block text-xs font-medium text-[#A0AEC0] mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[var(--surface-0)] border border-[var(--border-subtle)] rounded-xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Register External Agent</h2>
          <button onClick={onClose} className="text-[#718096] hover:text-white text-lg leading-none">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Agent Name *</label>
            <input data-testid="reg-agent-name" className={inputCls} placeholder="My External Agent" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Endpoint URL *</label>
            <input data-testid="reg-agent-url" className={inputCls} placeholder="https://agent.example.com/api" value={form.endpoint_url} onChange={e => setForm(p => ({ ...p, endpoint_url: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <input data-testid="reg-agent-desc" className={inputCls} placeholder="What does this agent do?" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Auth Method</label>
              <select data-testid="reg-agent-auth" className={inputCls + ' appearance-none cursor-pointer'} value={form.auth_method} onChange={e => setForm(p => ({ ...p, auth_method: e.target.value }))}>
                <option value="api_key">API Key</option>
                <option value="oauth">OAuth</option>
                <option value="bearer">Bearer Token</option>
                <option value="hmac">HMAC</option>
                <option value="none">None</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Trust Class</label>
              <select data-testid="reg-agent-trust" className={inputCls + ' appearance-none cursor-pointer'} value={form.trust_class} onChange={e => setForm(p => ({ ...p, trust_class: e.target.value }))}>
                <option value="external_registered">Registered</option>
                <option value="internal_trusted">Internal Trusted</option>
                <option value="unverified_remote">Unverified</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Capabilities (comma-separated)</label>
            <input data-testid="reg-agent-caps" className={inputCls} placeholder="data-analysis, report-generation, summarization" value={form.capabilities} onChange={e => setForm(p => ({ ...p, capabilities: e.target.value }))} />
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#A0AEC0] hover:text-white transition-colors">Cancel</button>
            <button
              type="submit"
              data-testid="reg-agent-submit"
              disabled={submitting}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-sm font-medium transition-colors disabled:opacity-50"
            >
              {submitting ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
              Register Agent
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-medium text-[#718096] uppercase tracking-wider mb-2">{title}</h4>
      {children}
    </div>
  );
}

export default ExternalAgentLibrary;
